import React from 'react'
import styled from 'styled-components/macro'

/*
 * Componentes visuais compartilhados do admin.
 * Referência de estilo: o modal de detalhes de solicitação.
 */

export const tokens = {
  text: '#26364a',
  textSoft: '#6b7f99',
  label: '#8a9cb5',
  border: '#e8edf4',
  borderSoft: '#f1f4f9',
  surface: '#ffffff',
  surfaceSoft: '#f8fafc',
  brand: 'hsla(199, 75%, 47%, 1)',
  radius: '12px'
}

/* ---------------------------------------------------------------- Página */

export const PageCard = styled.div`
  background: ${tokens.surface};
  border: 1px solid ${tokens.border};
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.05);
  padding: 24px;
  @media (max-width: 40em) {
    padding: 16px;
  }
`

const HeaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${tokens.text};
`

const HeaderCount = styled.span`
  margin-left: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #eef2f7;
  color: ${tokens.textSoft};
  font-size: 12px;
  font-weight: 600;
  vertical-align: middle;
`

export const PageHeader = ({ title, count, children }) => (
  <HeaderWrap>
    <HeaderTitle>
      {title}
      {(count || count === 0) && <HeaderCount>{count}</HeaderCount>}
    </HeaderTitle>
    {children ? <div>{children}</div> : null}
  </HeaderWrap>
)

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0 14px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${tokens.textSoft};
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${tokens.border};
  }
`

/* --------------------------------------------------------------- Filtros */

export const Toolbar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
`

/* ---------------------------------------------------------------- Status */

const STATUS_COLORS = {
  'PENDENTE': ['#fff4e0', '#a86a12'],
  'PENDENTE (CRAWLER)': ['#fff4e0', '#a86a12'],
  'CONCLUÍDA': ['#e6f6ec', '#1d7a45'],
  'CONCLUÍDA COM ERRO': ['#fdeaea', '#b3261e'],
  'ERRO': ['#fdeaea', '#b3261e']
}

const BadgeBase = styled.span`
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`

export const StatusBadge = ({ status }) => {
  const [bg, color] = STATUS_COLORS[status] || ['#eef2f7', '#5b6b80']
  return <BadgeBase $bg={bg} $color={color}>{status || '—'}</BadgeBase>
}

/* ------------------------------------------------------------ Vazio/erro */

const EmptyWrap = styled.div`
  padding: 44px 20px;
  text-align: center;
  color: #9db0c7;
  font-size: 14px;
  background: ${tokens.surfaceSoft};
  border: 1px dashed ${tokens.border};
  border-radius: ${tokens.radius};
`

export const EmptyState = ({ children = 'Nenhum registro encontrado' }) => (
  <EmptyWrap>{children}</EmptyWrap>
)

/* ----------------------------------------------------------- Modal (shell) */

export const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 860px;
  height: 100%;
`

/* sticky, nunca fixed: com position:fixed o cabeçalho se ancora à viewport
   e cobre o conteúdo do modal (bug corrigido no detalhe de solicitação). */
const ModalHeaderWrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 0 8px 0 24px;
  background: #f7f9fc;
  border-bottom: 1px solid ${tokens.border};
`

const ModalTitle = styled.div`
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: ${tokens.brand};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CloseButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.06); }
`

export const ModalHeader = ({ title, onRequestClose, closeIcon }) => (
  <ModalHeaderWrap>
    <ModalTitle>{title}</ModalTitle>
    <CloseButton onClick={onRequestClose}>{closeIcon}</CloseButton>
  </ModalHeaderWrap>
)

export const ModalBody = styled.div`
  flex: 1;
  padding: 24px;
  overflow: auto;
`

/* ------------------------------------------------------------ Campo (view) */

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
  margin-bottom: 26px;
`

export const Field = styled.div`
  background: ${tokens.surfaceSoft};
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 10px 14px;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`

export const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${tokens.label};
  margin-bottom: 4px;
`

export const FieldValue = styled.div`
  font-size: 14px;
  color: ${tokens.text};
  word-break: break-word;
  ${props => props.$mono && 'font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px;'}
`

/* --------------------------------------------------- Formulário (padrão) */

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`

export const FormRow = styled.div`
  ${props => props.$full && 'grid-column: 1 / -1;'}
`

export const FormLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${tokens.label};
  margin-bottom: 5px;
`

export const TextInput = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 12px;
  font-size: 14px;
  color: ${tokens.text};
  background: #fff;
  border: 1px solid ${props => (props.$error ? '#e3a6a1' : tokens.border)};
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease;
  &:focus { border-color: ${tokens.brand}; }
  &:disabled { background: ${tokens.surfaceSoft}; color: ${tokens.textSoft}; }
  &::placeholder { color: #b9c6d6; }
`

/* Mesmo desenho do TextInput. A seta nativa fica, para não reinventar
   acessibilidade de teclado num <select> customizado. */
export const SelectInput = styled.select`
  width: 100%;
  height: 42px;
  padding: 0 10px;
  font-size: 14px;
  color: ${props => (props.value ? tokens.text : '#b9c6d6')};
  background: #fff;
  border: 1px solid ${props => (props.$error ? '#e3a6a1' : tokens.border)};
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease;
  &:focus { border-color: ${tokens.brand}; }
  &:disabled { background: ${tokens.surfaceSoft}; color: ${tokens.textSoft}; }
  option { color: ${tokens.text}; }
`

/* Texto longo (descrição, horário de atendimento, uma alergia por linha). */
export const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.45;
  color: ${tokens.text};
  background: #fff;
  border: 1px solid ${props => (props.$error ? '#e3a6a1' : tokens.border)};
  border-radius: 8px;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s ease;
  &:focus { border-color: ${tokens.brand}; }
  &::placeholder { color: #b9c6d6; }
`

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${tokens.text};
  cursor: pointer;
  input { width: 16px; height: 16px; cursor: pointer; accent-color: ${tokens.brand}; }
`

/* Lista de vínculos (planos do médico, especialidades da clínica).
   Rolagem com altura fixa: são listas que crescem com o cadastro, e sem teto
   o formulário empurrava o botão de salvar para fora da tela. */
export const CheckboxGroup = styled.div`
  max-height: 148px;
  overflow-y: auto;
  padding: 10px 12px;
  border: 1px solid ${tokens.border};
  border-radius: 8px;
  background: #fff;
  display: grid;
  gap: 8px;
`

export const EmptyHint = styled.div`
  font-size: 13px;
  color: ${tokens.textSoft};
  padding: 4px 0;
`

/* Divisor de bloco dentro do formulário. Sem ele o cadastro completo vira uma
   parede de 20 campos sem hierarquia nenhuma. */
export const FormSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px solid ${tokens.border};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.brand};
  &:first-child { margin-top: 0; padding-top: 0; border-top: none; }
`

export const FieldError = styled.div`
  min-height: 16px;
  margin-top: 4px;
  font-size: 11px;
  color: #b3261e;
`

const BotaoBase = styled.button`
  height: 42px;
  padding: 0 22px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:disabled { opacity: 0.5; cursor: default; }
`

export const PrimaryButton = styled(BotaoBase)`
  border: 1px solid ${tokens.brand};
  background: ${tokens.brand};
  color: #fff;
`

export const GhostButton = styled(BotaoBase)`
  border: 1px solid ${tokens.border};
  background: #fff;
  color: ${tokens.textSoft};
`

/* Rodapé fixo do modal: os botões não somem ao rolar um formulário longo. */
export const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid ${tokens.border};
`
